import { Link } from "react-router-dom";
import { ClipboardCheck } from "lucide-react";
import { useSidebar } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTaskDashboard } from "@/contexts/TaskDashboardContext";
import { cn } from "@/lib/utils";

export function TaskSidebarMissionStrip() {
  const { state, isMobile } = useSidebar();
  const isCollapsed = !isMobile && state === "collapsed";
  const {
    dayProgress,
    counts,
    motivationalLabel,
    hasUrgentAttention,
    showMissionStrip,
  } = useTaskDashboard();

  if (!showMissionStrip) return null;

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Link
            to="/tasks"
            className={cn(
              "flex h-9 w-9 items-center justify-center rounded-md mx-auto",
              hasUrgentAttention
                ? "bg-destructive/15 text-destructive ring-2 ring-destructive/50 animate-pulse"
                : "bg-amber-500/15 text-amber-700 dark:text-amber-300",
            )}
            aria-label={`Missão: ${dayProgress.done}/${dayProgress.total}`}
          >
            <ClipboardCheck className="h-4 w-4" />
          </Link>
        </TooltipTrigger>
        <TooltipContent side="right">
          <p className="font-medium">Missão: {dayProgress.done}/{dayProgress.total}</p>
          {counts.overdue > 0 && (
            <p className="text-destructive text-xs">{counts.overdue} atrasada(s)</p>
          )}
        </TooltipContent>
      </Tooltip>
    );
  }

  const barColor = hasUrgentAttention
    ? "bg-destructive"
    : dayProgress.percent >= 100
      ? "bg-emerald-500"
      : "bg-gradient-to-r from-amber-500 to-amber-400";

  return (
    <div
      className={cn(
        "rounded-lg border p-2.5 space-y-2 mb-2",
        hasUrgentAttention
          ? "border-destructive/40 bg-destructive/5"
          : dayProgress.percent >= 100
            ? "border-emerald-500/30 bg-emerald-500/5"
            : "border-amber-500/25 bg-amber-500/5",
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <p className="text-[11px] font-semibold leading-tight">Sua missão de hoje</p>
        <span className="text-[10px] font-bold tabular-nums text-primary">
          {dayProgress.done}/{dayProgress.total}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted overflow-hidden">
        <div
          className={cn("h-full rounded-full transition-all duration-500", barColor)}
          style={{ width: `${dayProgress.percent}%` }}
        />
      </div>
      {hasUrgentAttention ? (
        <p className="text-[10px] font-medium text-destructive leading-snug">
          {counts.overdue} atrasada{counts.overdue === 1 ? "" : "s"} — resolver agora
        </p>
      ) : (
        <p className="text-[10px] text-muted-foreground leading-snug line-clamp-1">
          {motivationalLabel}
        </p>
      )}
      <Button variant="ghost" size="sm" className="h-7 w-full text-xs" asChild>
        <Link to="/tasks">Ir para tarefas</Link>
      </Button>
    </div>
  );
}
